import Close from '@mui/icons-material/Close';
import Replay from '@mui/icons-material/Replay';
import SaveIcon from '@mui/icons-material/Save';
import { AppBar, Button, Dialog, IconButton, Toolbar, Typography, useTheme } from '@mui/material';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import SignaturePad from 'signature_pad';

import { ActionComponentProps } from '../index';
import {
  SignatureCanvas,
  SignatureContainer,
  SignatureLine,
  SignatureMarker,
  SignaturePadArea,
  SignaturePadWrapper,
} from './styles';

type DialogSignatureConfirmationProps = ActionComponentProps & {
  persist: boolean;
  onConfirm: (imageBase64: string) => void;
};

export const DialogSignatureConfirmation = ({
  actionStarted,
  persist,
  onActionFinish,
  onConfirm,
}: DialogSignatureConfirmationProps) => {
  const theme = useTheme();

  const signaturePadWrapperRef = useRef<HTMLDivElement>(null);

  // Signature Pad library documentation: https://github.com/szimek/signature_pad
  const [signaturePad, setSignaturePad] = useState<SignaturePad | null>(null);
  const [signaturePadSize, setSignaturePadSize] = useState<{ width: number; height: number }>({
    width: 780,
    height: 400,
  });
  const [signatureBase64PNG, setSignatureBase64PNG] = useState<string>('');
  const [isSigned, setIsSigned] = useState<boolean>(false);

  const setCanvasRef = useCallback(
    (node: HTMLCanvasElement | null) => {
      const signaturePadInstance = node
        ? new SignaturePad(node, { backgroundColor: theme.palette.background.neutral })
        : null;

      setSignaturePad(signaturePadInstance);
    },
    [theme.palette.background.neutral],
  );

  const resizeObserver = useMemo(
    () =>
      new ResizeObserver((entries) => {
        entries.forEach((entry) => {
          setSignaturePadSize({
            width: entry.contentRect.width,
            height: entry.contentRect.height,
          });
        });
      }),
    [],
  );

  useEffect(() => {
    if (signaturePad) {
      signaturePad.addEventListener('endStroke', () => {
        setIsSigned(!signaturePad.isEmpty());
        setSignatureBase64PNG(signaturePad.toDataURL());
      });

      return () => {
        signaturePad.off();
        setSignatureBase64PNG('');
      };
    } else {
      setIsSigned(false);
    }
  }, [signaturePad]);

  useEffect(() => {
    if (!signaturePadWrapperRef.current) return;
    resizeObserver.observe(signaturePadWrapperRef.current);

    return () => {
      resizeObserver.disconnect();
    };
  }, [resizeObserver, signaturePad]);

  // Handling resize of the Signature Pad canvas.
  // Just after resize restoring the signature image since
  // browsers automatically clear canvases on resize.
  useEffect(() => {
    if (!signaturePad || !signatureBase64PNG) return;
    signaturePad.clear();
    signaturePad.fromDataURL(signatureBase64PNG, {
      ratio: 1,
      width: signaturePadSize.width,
      height: signaturePadSize.height,
    });
  }, [signaturePad, signaturePadSize, signatureBase64PNG]);

  const clearSignature = () => {
    if (signaturePad) {
      signaturePad.clear();
      setSignatureBase64PNG('');
      setIsSigned(false);
    }
  };

  const submitSignature = () => {
    if (signaturePad) {
      onConfirm(signaturePad.toDataURL('image/jpeg'));
    }
  };

  return (
    <Dialog fullScreen open={actionStarted} onClose={() => !persist && onActionFinish()}>
      <AppBar position="relative" sx={{ bgcolor: 'background.neutral' }} elevation={1}>
        <Toolbar>
          <IconButton edge="start" onClick={onActionFinish} disabled={persist} aria-label="Close">
            <Close />
          </IconButton>
          <Typography variant="h6" sx={{ flex: 1, ml: 1 }}>
            Capture Signature
          </Typography>
          <Button
            onClick={submitSignature}
            disabled={!isSigned || persist}
            loading={persist}
            startIcon={persist && <SaveIcon />}
            loadingPosition={persist ? 'start' : undefined}
            variant="contained"
            color="primary"
          >
            Complete
          </Button>
        </Toolbar>
      </AppBar>

      <SignatureContainer>
        <SignaturePadArea>
          <SignaturePadWrapper ref={signaturePadWrapperRef}>
            <SignatureCanvas width={signaturePadSize.width} height={signaturePadSize.height} ref={setCanvasRef} />
            <SignatureMarker color="disabled" />
            <SignatureLine />
          </SignaturePadWrapper>

          <Button variant="text" color="inherit" startIcon={<Replay />} onClick={clearSignature} sx={{ my: 1 }}>
            Clear
          </Button>
        </SignaturePadArea>
      </SignatureContainer>
    </Dialog>
  );
};
